import { useState, useEffect, useRef } from 'react';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import * as fontkit from 'fontkit';

async function loadFont(url) {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Font fetch failed: ${response.status} ${response.statusText}`);
    }
    const fontBytes = await response.arrayBuffer();
    console.log('Font loaded successfully:', url);
    return fontBytes;
  } catch (error) {
    console.error('Font loading error:', error);
    throw error;
  }
}


function wrapText(text, font, fontSize, maxWidth) {
  const words = text.split(' ');
  const lines = [];
  let currentLine = '';

  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const testWidth = font.widthOfTextAtSize(testLine, fontSize);

    if (testWidth <= maxWidth) {
      currentLine = testLine;
    } else {
      if (currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        // Single word is too long, force it on its own line
        lines.push(word);
        currentLine = '';
      }
    }
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
}

function wrapTextForimg(text, ctx, fontSize, maxWidth) {
  const words = text.split(' ');
  const lines = [];
  let currentLine = '';

  words.forEach((word) => {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const testWidth = ctx.measureText(testLine).width;
    if (testWidth <= maxWidth) {
      currentLine = testLine;
    } else {
      if (currentLine) lines.push(currentLine);
      currentLine = word;
    }
  });

  if (currentLine) lines.push(currentLine);
  return lines;
}
async function preloadCertificateFont(fontUrl) {
  const response = await fetch(fontUrl);
  if (!response.ok) throw new Error("Font fetch failed");
  const fontBytes = await response.arrayBuffer();

  // Register font for Canvas
  const fontFace = new FontFace("Kunstler Script", fontBytes);
  await fontFace.load();
  document.fonts.add(fontFace);

  // Ensure browser acknowledges it
  await document.fonts.ready;

  return fontBytes;
}


export function useGenerateCertificate({
  userName,
  courseName,
  isMumbai,
  trigger,
}) {
    const [generating, setGenerating] = useState(false);
    const [certificatePdfUrl, setCertificatePdfUrl] = useState(null);
    const [certificateImageUrl, setCertificateImageUrl] = useState(null);
    const [downloadDate, setDownloadDate] = useState(null);
    const [fontLoaded, setFontLoaded] = useState(false);
    const [fontBytes, setFontBytes] = useState(null);
    const uidRef = useRef(null);
    const AWS = "https://s3.us-east-1.amazonaws.com/hunarho.com/KUNSTLER.TTF";

    function getUID() {
      if (!uidRef.current) {
        uidRef.current = generateCertificateUID();
      }
      return uidRef.current;
    }

    useEffect(() => {
      let mounted = true;

      (async () => {
        try {
          const bytes = await preloadCertificateFont(AWS);
          if (mounted) setFontBytes(bytes);
        } catch (err) {
          console.error("Font preload failed", err);
        }
      })();

      return () => {
        mounted = false;
      };
    }, []);

    useEffect(() => {
      async function preloadFont() {
        const loaded = await loadFont(AWS)
        setFontLoaded(loaded);
      }
      preloadFont();
    }, []);

    useEffect(() => {
        if (!trigger || !fontLoaded) return;
        (async () => {
          setGenerating(true);
          try {
            await generateCertificate(fontBytes);
            await generateCertificateImage();
            setDownloadDate(new Date());
          } catch (e) {
            console.error(e);
          } finally {
            setGenerating(false);
          }
        })();
    }, [trigger, fontLoaded]);

    function generateCertificateUID() {
      const studentId = sessionStorage.getItem('id');
      const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
      let randomPart = "";
      
      for (let i = 0; i < 6; i++) {
        const randIndex = Math.floor(Math.random() * chars.length);
        randomPart += chars[randIndex];
      }

      return `H${studentId}${randomPart}`;
    }

    async function generateCertificate() {
      try {
        const templateFile = isMumbai ? 'https://s3.us-east-1.amazonaws.com/hunarho.com/certificatem.pdf' : 'https://s3.us-east-1.amazonaws.com/hunarho.com/certificate3nov.pdf';
        const existingPdfBytes = await fetch(templateFile).then((res) => {
          if (!res.ok) throw new Error(`Failed to fetch ${templateFile}`);
          return res.arrayBuffer();
        });

        const pdfDoc = await PDFDocument.create();
        pdfDoc.registerFontkit(fontkit);

        const [templatePage] = await PDFDocument.load(existingPdfBytes).then(d => d.getPages());
        const embeddedPage = await pdfDoc.embedPage(templatePage);

        const page = pdfDoc.addPage([595.28, 841.89]);
        const { width, height } = page.getSize();

        const scaleX = width / templatePage.getSize().width;
        const scaleY = height / templatePage.getSize().height;
        const scale = Math.min(scaleX, scaleY);

        // Calculate vertical centering offset
        const scaledHeight = templatePage.getSize().height * scale;
        const yOffset = (height - scaledHeight) / 2; // centers vertically

        page.drawPage(embeddedPage, {
          x: 0,
          y: yOffset,
          xScale: scale,
          yScale: scale,
        });

        const kunstlerFont = await pdfDoc.embedFont(fontBytes);
        const calibriFont = await pdfDoc.embedFont(StandardFonts.Helvetica);

        const nameFontSize = 68;
        const nameWidth = kunstlerFont.widthOfTextAtSize(userName, nameFontSize);
        const nameX = (width - nameWidth) / 2;
        const nameY = height * 0.58;
        page.drawText(userName, {
          x: nameX,
          y: nameY,
          size: nameFontSize,
          font: kunstlerFont,
          color: rgb(55 / 255, 65 / 255, 81 / 255),
        });

        // --- Course ---
        const courseFontSize = 20;
        const maxCourseWidth = width * 0.9;
        const courseLines = wrapText(courseName, calibriFont, courseFontSize, maxCourseWidth);
        const lineHeight = courseFontSize * 1.2;
        const startY = height * 0.41 + (courseLines.length * lineHeight) / 2 - lineHeight;

        courseLines.forEach((line, i) => {
          const lineWidth = calibriFont.widthOfTextAtSize(line, courseFontSize);
          const lineX = (width - lineWidth) / 2;
          const lineY = startY - i * lineHeight;
          page.drawText(line, {
            x: lineX,
            y: lineY + 3,
            size: courseFontSize,
            font: calibriFont,
            color: rgb(55 / 255, 65 / 255, 81 / 255),
          });
        });

        // --- Date ---
        const issueDate = new Date().toLocaleDateString('en-GB');
        page.drawText(issueDate, {
          x: width - 160,
          y: 137,
          size: 12,
          font: calibriFont,
          color: rgb(0, 0, 0),
        });

        // --- UID ---
        const uid = getUID();
        page.drawText(`Certificate UID: ${uid}`, {
          x: 408,
          y: 42,
          size: 12,
          font: calibriFont,
          color: rgb(0, 0, 0),
        });

        // Save PDF
        const pdfDataUri = await pdfDoc.saveAsBase64({ dataUri: true });
        setCertificatePdfUrl(pdfDataUri);

      } catch (err) {
        console.error('Certificate generation error:', err);
      }
    }


    async function generateCertificateImage() {
        const uid = `Certificate UID: ${getUID()}`;
        try {
            const offscreenCanvas = document.createElement('canvas');
            const ctx = offscreenCanvas.getContext('2d');

            const certificateImage = new Image();
            certificateImage.crossOrigin = "anonymous";
            certificateImage.src = isMumbai ? 'https://s3.us-east-1.amazonaws.com/hunarho.com/mumbai.jpeg' : 'https://s3.us-east-1.amazonaws.com/hunarho.com/cert.jpeg';

            await new Promise((resolve, reject) => {
              certificateImage.onload = resolve;
              certificateImage.onerror = reject;
            });

            offscreenCanvas.width = certificateImage.width;
            offscreenCanvas.height = certificateImage.height;

            if (!ctx) {
              throw new Error('Error creating canvas context');
            }

            ctx.drawImage(certificateImage, 0, 0);

            const { width, height } = offscreenCanvas;

            ctx.font = '40px Helvetica';
            ctx.fillStyle = 'rgb(55,65,81)';

            const maxCourseWidth = width * 0.9;
            const courseLines = wrapTextForimg(courseName, ctx, 90, maxCourseWidth);

            const courseLineHeight = 90 * 1.8;
            const courseTotalHeight = courseLines.length * courseLineHeight;
            const courseStartY = height * 0.65 + (courseTotalHeight / 2) - courseLineHeight;
            courseLines.forEach((line, i) => {
              const lineWidth = ctx.measureText(line).width;
              const lineX = (width - lineWidth) / 2;
              const lineY = courseStartY - (i * courseLineHeight);
              ctx.fillText(line, lineX, lineY);
            });

            // Student Name
            ctx.font = '102px "Kunstler Script" ';
            ctx.fillStyle = 'rgb(55,65,81)';
            const nameWidth = ctx.measureText(userName).width;
            const nameX = (width - nameWidth) / 2;
            const nameY = height * 0.43;
            ctx.fillText(userName, nameX, nameY);
            // issueDate
            const issueDate = new Date().toLocaleDateString('en-GB');
            ctx.font = '33px Helvetica';
            ctx.fillStyle = 'black';
            // const dateWidth = ctx.measureText(issueDate).width;
            const dateX = 828;
            const dateY = 1360;
            ctx.fillText(issueDate, dateX, dateY);
            
            // UID
            // const uidWidth = ctx.measureText(uid).width;
            ctx.font = '23px Helvetica';
            ctx.fillStyle = 'black';
            const uidX = 829;
            const uidY = 1560;
            ctx.fillText(uid, uidX, uidY);

            const dataUrl = offscreenCanvas.toDataURL();
            setCertificateImageUrl(dataUrl);
        } catch (error) {
            console.error('Error generating certificate image:', error);
        }
    }

    return {
        generating,
        certificatePdfUrl,
        certificateImageUrl,
        downloadDate,
        fontLoaded
    };
}
